<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { useTyping } from "@/composables/useTyping";
import { PROFILE_FIELDS } from "@/store/modules/personalInfo";
import DetailField from "./DetailField.vue";

// The details every make can use: grouped the way people think of them, plus any of
// their own. Saving goes back to where they came from.
const store = useStore();

const GROUPS = [
  {
    title: "About you",
    note: "So makes can speak for you",
    icon: "user",
    fields: [
      {
        field: "name",
        label: "Full name",
        placeholder: "Jane Smith",
        autocomplete: "name",
      },
      {
        field: "gender",
        label: "Gender",
        placeholder: "Woman, man, non-binary...",
        autocomplete: "sex",
      },
      {
        field: "country",
        label: "Country",
        hint: "For spelling, dates and money",
        placeholder: "United Kingdom",
        autocomplete: "country-name",
        wide: true,
      },
    ],
  },
  {
    title: "Work",
    note: "For letters, emails and anything official",
    icon: "briefcase",
    fields: [
      {
        field: "job",
        label: "Job title",
        placeholder: "Event planner",
        autocomplete: "organization-title",
      },
      {
        field: "company",
        label: "Company or organisation",
        placeholder: "Smith Events Ltd",
        autocomplete: "organization",
      },
      {
        field: "signOff",
        label: "How you sign off",
        hint: "Goes at the end of letters and emails",
        placeholder: "Jane Smith, Director",
        wide: true,
      },
    ],
  },
  {
    title: "Contact",
    note: "Filled in wherever a make needs them",
    icon: "at",
    fields: [
      {
        field: "email",
        label: "Email",
        hint: "The one to put in makes",
        placeholder: "you@email.com",
        type: "email",
        autocomplete: "email",
      },
      {
        field: "phone",
        label: "Phone",
        hint: "For letters and contracts",
        placeholder: "07700 900123",
        type: "tel",
        autocomplete: "tel",
      },
      {
        field: "address",
        label: "Address",
        placeholder: "1 High Street, Bath, BA1 1AA",
        autocomplete: "street-address",
        wide: true,
      },
      {
        field: "website",
        label: "Website",
        placeholder: "smithevents.co.uk",
        inputmode: "url",
        autocomplete: "url",
        wide: true,
      },
    ],
  },
];

const FIELDS = [
  "name",
  "gender",
  "email",
  "job",
  "country",
  "extraInfo",
  ...PROFILE_FIELDS,
];
const stored = () =>
  Object.fromEntries(
    FIELDS.map((field) => [field, store.state.personalInfo[field] || ""])
  );
const form = reactive(stored());

// Details can arrive after the page opens; show them unless something's been typed
const touched = ref(false);
watch(
  () => store.state.personalInfo.hasLoaded,
  (loaded) => {
    if (loaded && !touched.value) Object.assign(form, stored());
  }
);

const loading = computed(() => store.state.personalInfo.loading);
const isMobile = computed(() => store.state.isMobile);
// While typing on a phone the keyboard is up, so the Save bar gets out of the way
const { typing } = useTyping();
const totalMakes = computed(() => store.state.personalInfo.totalMakes);
const categories = computed(() => store.state.personalInfo.categories);
const removedCategories = computed(
  () => store.state.personalInfo.removedCategories
);
const lastRemoved = computed(
  () => removedCategories.value[removedCategories.value.length - 1]
);

// Their own details, added here without a pop-up
const newName = ref("");
const newAnswer = ref("");
const addError = ref("");
const nameInput = ref(null);
const addDetail = () => {
  const name = newName.value.trim();
  if (!name) {
    addError.value = "Give it a name first, like Pet's name";
    nameInput.value?.focus();
    return;
  }
  if (name.length > 60) {
    addError.value = "Keep the name under 60 characters";
    return;
  }
  const existing = categories.value.find(
    (category) => category.name.toLowerCase() === name.toLowerCase()
  );
  if (existing) {
    addError.value = `You already have ${existing.name}`;
    return;
  }
  store.dispatch("personalInfo/addCategory", {
    name,
    answer: newAnswer.value.trim(),
  });
  touched.value = true;
  newName.value = "";
  newAnswer.value = "";
  addError.value = "";
  nameInput.value?.focus();
};
const removeDetail = (index) => {
  touched.value = true;
  store.dispatch("personalInfo/removeCategory", index);
};
const undoRemove = () => store.dispatch("personalInfo/undoRemoveCategory");

// Which button is busy: "save" or "learn"
const busy = ref("");
const learn = async () => {
  busy.value = "learn";
  await store.dispatch("personalInfo/getMagicInfo");
  busy.value = "";
};

// The same as the browser's Back button
const back = () => history.back();

const save = async () => {
  busy.value = "save";
  await store.dispatch("personalInfo/saveInfo", {
    ...form,
    categories: categories.value,
  });
  busy.value = "";
  back();
};
</script>

<template>
  <section
    class="mt-8 lg:mt-12 lg:grid lg:grid-cols-3 lg:gap-11"
    :class="{ 'pb-28': isMobile }"
  >
    <div class="lg:col-span-2" @input="touched = true">
      <header>
        <div class="flex items-center justify-between gap-4">
          <button
            type="button"
            class="flex items-center text-sm font-bold uppercase text-accentPrimary"
            @click="back"
          >
            <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-2" />
            Back
          </button>
          <button
            v-if="!isMobile"
            type="button"
            class="flex items-center rounded-full bg-accentPrimary px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-accentPrimaryLight disabled:bg-secondary"
            :disabled="Boolean(busy)"
            @click="save"
          >
            <font-awesome-icon :icon="['fas', 'check']" class="mr-2" />
            {{ busy === "save" ? "Saving..." : "Save details" }}
          </button>
        </div>
        <h2 class="mt-4 text-2xl font-bold text-primary lg:text-3xl">
          Your details
        </h2>
        <p class="mt-1 max-w-xl text-sm text-secondary sm:text-base">
          Saved once and used in every make, so letters come with your address
          and emails with your sign-off. Fill in as much or as little as you
          like.
        </p>
      </header>

      <div
        v-if="totalMakes >= 5"
        class="mt-6 flex flex-col gap-3 rounded-2xl bg-primary p-4 text-white sm:flex-row sm:items-center sm:p-5"
      >
        <font-awesome-icon
          :icon="['fas', 'wand-magic-sparkles']"
          class="hidden text-2xl text-accentSecondary sm:block"
        />
        <div class="min-w-0 flex-1">
          <p class="font-bold">Let the genie fill some in</p>
          <p class="text-sm text-secondaryLight">
            It can suggest details from the {{ totalMakes }} makes you've made.
          </p>
        </div>
        <button
          type="button"
          class="flex shrink-0 items-center justify-center rounded-full bg-accentSecondary px-5 py-2.5 text-sm font-bold text-primary transition-colors hover:bg-yellow-300 disabled:opacity-60"
          :disabled="Boolean(busy)"
          @click="learn"
        >
          <font-awesome-icon
            :icon="['fas', 'wand-magic-sparkles']"
            class="mr-2 sm:hidden"
          />
          {{
            busy === "learn" ? "Looking at your makes..." : "Suggest details"
          }}
        </button>
      </div>

      <div class="mt-6 space-y-4">
        <section
          v-for="group in GROUPS"
          :key="group.title"
          class="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6"
        >
          <div class="flex items-center gap-3">
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accentSecondaryLight text-primary"
              aria-hidden="true"
            >
              <font-awesome-icon :icon="['fas', group.icon]" />
            </span>
            <div>
              <h3 class="font-bold text-primary">{{ group.title }}</h3>
              <p class="text-xs text-secondary">{{ group.note }}</p>
            </div>
          </div>
          <div class="mt-4 grid gap-4 sm:grid-cols-2">
            <div
              v-for="item in group.fields"
              :key="item.field"
              :class="{ 'sm:col-span-2': item.wide }"
            >
              <DetailField
                v-model="form[item.field]"
                :label="item.label"
                :hint="item.hint"
                :placeholder="item.placeholder"
                :type="item.type || 'text'"
                :inputmode="item.inputmode"
                :autocomplete="item.autocomplete"
                :disabled="loading"
              />
            </div>
          </div>
        </section>

        <section class="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
          <div class="flex items-center gap-3">
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accentSecondaryLight text-primary"
              aria-hidden="true"
            >
              <font-awesome-icon :icon="['fas', 'list-check']" />
            </span>
            <div>
              <h3 class="font-bold text-primary">Your own details</h3>
              <p class="text-xs text-secondary">
                Anything above misses, like a pet's name or a car registration
              </p>
            </div>
          </div>
          <ul v-if="categories.length" class="mt-4 space-y-4">
            <li
              v-for="(category, index) in categories"
              :key="category.name"
              class="flex items-end gap-2"
            >
              <div class="min-w-0 flex-1">
                <DetailField
                  v-model="category.answer"
                  :label="category.name"
                  :placeholder="category.name"
                  :disabled="loading"
                />
              </div>
              <button
                type="button"
                class="flex h-[3.125rem] w-11 shrink-0 items-center justify-center rounded-xl text-secondary transition-colors hover:bg-red-50 hover:text-red-600"
                :aria-label="`Remove ${category.name}`"
                :title="`Remove ${category.name}`"
                @click="removeDetail(index)"
              >
                <font-awesome-icon :icon="['fas', 'trash-can']" />
              </button>
            </li>
          </ul>
          <p
            v-if="lastRemoved"
            class="mt-3 flex items-center gap-2 text-sm text-secondary"
            role="status"
          >
            Removed {{ lastRemoved.name }}.
            <button
              type="button"
              class="flex items-center font-bold text-accentPrimary hover:underline"
              @click="undoRemove"
            >
              <font-awesome-icon :icon="['fas', 'rotate-left']" class="mr-1" />
              Undo
            </button>
          </p>
          <div class="mt-4 rounded-xl bg-gray-10 p-3 sm:p-4">
            <p class="text-sm font-bold text-primary">Add a detail</p>
            <div class="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <input
                ref="nameInput"
                v-model="newName"
                type="text"
                aria-label="What the detail is"
                placeholder="What it is, like Pet's name"
                maxlength="80"
                class="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-primary placeholder:text-secondaryLight focus:border-accentPrimary focus:outline-none focus:ring-1 focus:ring-accentPrimary"
                @keydown.enter.prevent="addDetail"
              />
              <input
                v-model="newAnswer"
                type="text"
                aria-label="The detail"
                placeholder="The detail, like Biscuit"
                class="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-primary placeholder:text-secondaryLight focus:border-accentPrimary focus:outline-none focus:ring-1 focus:ring-accentPrimary"
                @keydown.enter.prevent="addDetail"
              />
              <button
                type="button"
                class="flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-secondary"
                @click="addDetail"
              >
                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1.5" />
                Add
              </button>
            </div>
            <p
              v-if="addError"
              class="mt-2 text-sm font-medium text-red-500"
              role="alert"
            >
              {{ addError }}
            </p>
          </div>
        </section>

        <section class="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
          <DetailField
            v-model="form.extraInfo"
            multiline
            label="Anything else the genie should know"
            hint="How you like to write, allergies, your children's names..."
            placeholder="I keep things short and friendly. My children are Sam and Ellie."
            :disabled="loading"
          />
        </section>
      </div>

      <!-- On phones it's pinned to the bottom of the screen, so saving is always in
        reach; it lives outside the sliding page, whose movement would throw it off -->
      <Teleport to="body" :disabled="!isMobile">
        <div
          v-show="!(isMobile && typing)"
          :class="
            isMobile
              ? 'fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_16px_rgba(35,31,66,0.06)] backdrop-blur md:px-16'
              : 'mb-12 mt-6 rounded-2xl border border-gray-200 bg-white px-5 py-3'
          "
        >
          <div class="flex items-center justify-between gap-4">
            <p
              class="hidden text-sm text-secondary sm:block"
              aria-live="polite"
            >
              {{ touched ? "You have changes to save" : "Nothing changed yet" }}
            </p>
            <div class="flex w-full items-center gap-2 sm:w-auto">
              <button
                type="button"
                class="rounded-full px-4 py-3 text-sm font-bold text-secondary transition-colors hover:text-primary"
                @click="back"
              >
                Cancel
              </button>
              <button
                type="button"
                class="flex flex-1 items-center justify-center rounded-full bg-accentPrimary px-6 py-3 text-base font-bold text-white transition-colors hover:bg-accentPrimaryLight disabled:bg-secondary sm:flex-none"
                :disabled="Boolean(busy)"
                @click="save"
              >
                <font-awesome-icon :icon="['fas', 'check']" class="mr-2" />
                {{ busy === "save" ? "Saving..." : "Save details" }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>

    <div class="hidden lg:block">
      <img
        class="sticky top-24 mx-auto h-[266px] animate-fade_in_genie"
        src="/genie-crossed.svg"
        alt=""
      />
    </div>
  </section>
</template>
